import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  signal,
  Inject,
  Input,
  computed,
} from "@angular/core";
import {
  AccessGrant,
  AccessRule,
  FileExtensions,
  FileInfo,
  FileSystemObjectType,
  FileSystemTreeNode,
  OperationMode,
  AccessRuleType,
  SelectionMode,
  DriveType,
  AccessRuleOperation,
} from "./types";
import { interval, Subscription } from "rxjs";
import { startWith } from "rxjs/operators";
import { differenceBy } from "lodash-es";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { FileManagerService, SYSTEM_DRIVE } from "./file-manager.service";
import { ConfirmDialogComponent, ConfirmDialogConfig } from "./confirm-dialog.component";
import { hasExtension } from "../../_services/image-upload/inspection-file.validator";
import { TranslateService } from "@ngx-translate/core";
import { getFilePathInfo } from "../../_utils/file-system-utils";

type TreeNodeEvent = {
  node: FileSystemTreeNode;
  originalEvent?: PointerEvent;
};

const DETECT_DEVICES_INTERVAL = 10000; // 10 seconds

const DEFAULT_ACCESS_RULE: AccessRule = {
  operation: AccessRuleOperation.Or,
  rules: [
    {
      type: AccessRuleType.Drive,
      driveLetter: `${SYSTEM_DRIVE}:`,
      access: AccessGrant.Allow,
      defaultContentAccess: AccessGrant.Deny
    },
    {
      type: AccessRuleType.Drive,
      driveType: DriveType.Removable,
      access: AccessGrant.Allow,
      defaultContentAccess: AccessGrant.Allow
    },
  ],
};

const mapNodeToKey = (node: FileSystemTreeNode): string => node.key;

export type FileManagerConfig = {
  accessRule?: AccessRule;
  operationMode: OperationMode;
  selectionMode: SelectionMode;
  extensions: FileExtensions;
  fileName?: string;
  handleImportedSelection?: ((selected: File[]) => void | Promise<void>);
  handleExportedSelection?: ((selected: FileInfo[]) => void | Promise<void>);
};

@Component({
    selector: "app-file-manager",
    templateUrl: "./file-manager.component.html",
    styleUrl: "./file-manager.component.scss",
    standalone: false
})
export class FileManagerComponent implements OnInit, OnDestroy {
  static DEFAULT_WINDOW_SIZE = {
    width: "1280px",
    height: "720px",
  };

  readonly OperationMode = OperationMode;
  readonly SelectionMode = SelectionMode;

  @Input() private accessRule: AccessRule;

  @Input() operationMode = signal<OperationMode>(OperationMode.Import);

  @Input() selectionMode = signal<SelectionMode>(SelectionMode.SingleFileOrFolder);

  @Input() extensions: Set<string>;

  @Input() handleImportedSelection: ((selected: File[]) => void);

  @Input() handleExportedSelection: ((selected: FileInfo[]) => void);

  @Input() fileName = signal<string>("");
  @Input() private folderPath = signal<string>("");

  @Output() uploaded = new EventEmitter<boolean>();

  // Component state & UI fields
  private subscription: Subscription;

  private readonly currentSelectionMode;

  isLoading = signal<boolean>(false);

  isTreeLoaded = signal<boolean>(false);

  enableActionButton = computed<boolean>((): boolean => {
    let enableButton = false;

    switch (this.operationMode()) {
      case OperationMode.Import:
        // In this operation mode. Activate action button as long as there are items selected.
        enableButton = this.selectedNodes().length > 0;
        break;

      case OperationMode.Export:
        // In this operation mode. Activate action button as long as a folder/file name have been selected/set and file name is valid.
        const [folderPath, fileName] = [this.folderPath(), this.fileName()];
        enableButton = folderPath.length > 0 && fileName.length > 0 && hasExtension(fileName, this.extensions);
        break;
    }

    return enableButton;
  });

  fileSystemTree = signal<FileSystemTreeNode[]>([]);

  selectedNodes = signal<FileSystemTreeNode[]>([]);

  selectedKeys = signal<Set<string>>(new Set<string>());

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<FileManagerComponent>,
    private confirmDialogRef: MatDialogRef<ConfirmDialogComponent>,
    private fileManagerService: FileManagerService,
    private translateService: TranslateService,
  ) {
    const {
      accessRule,
      operationMode,
      selectionMode,
      extensions,
      fileName,
      handleImportedSelection,
      handleExportedSelection
    }: FileManagerConfig = this.data ?? {};

    this.accessRule = accessRule ?? DEFAULT_ACCESS_RULE;

    if (operationMode) {
      this.operationMode.set(operationMode);
    }

    if (selectionMode) {
      this.selectionMode.set(selectionMode);
    }

    if (extensions) {
      this.extensions = this.fileManagerService.normalizeExtensions(extensions);
    }

    // Import mode optional fields
    if (operationMode === OperationMode.Import) {
      this.handleImportedSelection = handleImportedSelection;
    }

    // Export mode optional fields
    if (operationMode === OperationMode.Export) {
      this.handleExportedSelection = handleExportedSelection;

      if (fileName) {
        this.fileName.set(fileName);
      }
    }

    this.currentSelectionMode = this.fileManagerService.getSelectionModeFlags(this.selectionMode());
  }

  async ngOnInit() {
    this.subscription = interval(DETECT_DEVICES_INTERVAL)
      .pipe(startWith(0))
      .subscribe(this.listDevices);
  }

  ngOnDestroy() {
    this.dialogRef?.close();
    this.confirmDialogRef?.close();
    this.subscription?.unsubscribe();
  }

  closeModal() {
    this.dialogRef.close();
  }

  async collapseAll() {
    this.fileSystemTree().forEach((node) => {
      this.toggleNodeContent(node, false);
    });
  }

  async expandAll() {
    this.fileSystemTree().forEach((node) => {
      this.toggleNodeContent(node, true);
    });
  }

  async onNodeExpand({ node }: TreeNodeEvent) {
    await this.toggleNodeContent(node, true);
  }

  async onNodeCollapse({ node }: TreeNodeEvent) {
    await this.toggleNodeContent(node, false);
  }

  async onNodeSelect({ node }: TreeNodeEvent) {
    await this.toggleNodeSelect(node);
  }

  async onNodeUnselect({ node }: TreeNodeEvent) {
    await this.toggleNodeSelect(node);
  }

  async refreshView() {
    try {
      this.isLoading.set(true);
      await this.listDevices();
    } finally {
      this.isLoading.set(false);
    }
  }

  // Import selected file(s) / folder(s)
  async importFiles(): Promise<void> {
    if (this.selectedNodes().length === 0) {
      return;
    }

    const selected = Array.from(this.selectedKeys());

    let filesToImport: File[] = [];

    switch (this.selectionMode()) {
      case SelectionMode.SingleFile:
      case SelectionMode.MultipleFiles: {
        filesToImport = await this.fileManagerService.readFiles(selected);

        break;
      }

      case SelectionMode.SingleFolder:
      case SelectionMode.MultipleFolders: {
        const promises = selected.map(async (selectedFolder) => {
          const discoveredFiles = await this.fileManagerService.scanFolderFiles(
            selectedFolder,
            this.extensions
          );
          const files = await this.fileManagerService.readFiles(
            discoveredFiles,
            selectedFolder
          );

          filesToImport.push(...files);
        });

        await Promise.all(promises);

        break;
      }
    }

    if (filesToImport.length === 0) {
      return;
    }

    this.closeModal();

    await this.handleImportedSelection?.(filesToImport);
  }

  async exportFiles(): Promise<void> {
    const [folderPath, fileName] = [this.folderPath(), this.fileName()];

    const [{ exists }] = await this.fileManagerService.filesExist([{ folderPath, fileName }]);

    if (exists) {
      this.openConfirmDialog(fileName);
    } else {
      await this.handleExportedSelection?.([{ folderPath, fileName }]);
      this.closeModal();
    }
  }

  openConfirmDialog(fileName: string) {
    const message = this.translateService.instant("fileManager.fileOverwriteConfirmationMessage", { fileName });

    const confirmDialogConfig: MatDialogConfig<ConfirmDialogConfig> = {
      ...ConfirmDialogComponent.DEFAULT_WINDOW_SIZE,
      data: {
        message
      }
    };

    this.confirmDialogRef = this.dialog.open(ConfirmDialogComponent, confirmDialogConfig);

    this.confirmDialogRef.afterClosed()
      .subscribe(async (result) => {
        if (result === true) {
          const [folderPath, fileName] = [this.folderPath(), this.fileName()];
          await this.handleExportedSelection?.([{ folderPath, fileName }]);
          this.closeModal();
        }
      });
  }

  private toggleNodeContent = async (
    node: FileSystemTreeNode,
    isExpanded: boolean
  ) => {
    if (node.leaf) {
      return;
    }

    const { accessRule, extensions } = this;

    const childrenNotLoaded = node.children === null;

    const isVolumeOrDirectory = [FileSystemObjectType.Volume, FileSystemObjectType.Folder].includes(node.data.type);

    const listNodeChildren = childrenNotLoaded && isVolumeOrDirectory;

    if (listNodeChildren) {
      node.loading = true;

      const nodes = await this.fileManagerService.listFilesAndFolders({
        accessRule,
        operationMode: this.operationMode(),
        selectionMode: this.selectionMode(),
        extensions,
        folderPath: node.key
      });

      node.children = nodes;

      node.loading = false;
    }

    node.expanded = isExpanded;
  };

  private listDevices = async () => {
    const { accessRule } = this;

    const operationMode = this.operationMode();

    const online = await this.fileManagerService.getOnlineDisksAndVolumes({ accessRule, operationMode });

    const displayed = this.fileSystemTree();

    const volumesToAdd = differenceBy(online, displayed, mapNodeToKey);

    const volumesToRemove = differenceBy(displayed, online, mapNodeToKey);

    if (volumesToAdd.length > 0 || volumesToRemove.length > 0) {
      // Merge fetched data with currently displayed data
      let fileSystemTree: FileSystemTreeNode[] = structuredClone(displayed);

      if (volumesToAdd.length > 0) {
        // Add new volumes with empty node trees
        fileSystemTree = fileSystemTree.concat(volumesToAdd);
      }

      if (volumesToRemove.length > 0) {
        // Remove volumes no longer detected
        const volumeKeysToRemove = volumesToRemove.map(mapNodeToKey);

        fileSystemTree = fileSystemTree.filter(
          (node) => !volumeKeysToRemove.includes(node.key)
        );
      }

      this.fileSystemTree.set(fileSystemTree);
    }

    if (!this.isTreeLoaded()) {
      this.isTreeLoaded.set(true);
    }
  };

  // Mark selected nodes as checked/unchecked
  private toggleSelectedNodes(nodes: FileSystemTreeNode[], isChecked: boolean) {
    nodes.forEach((node) => {
      node.checked = isChecked;
    });
  }

  private updateSelectedNodes(selectedNodesUpdate: FileSystemTreeNode[]) {
    // Mark selected nodes as checked
    this.toggleSelectedNodes(selectedNodesUpdate, true);

    this.selectedNodes.set(selectedNodesUpdate);

    // Update selected keys
    const keys = new Set<string>(selectedNodesUpdate.map(mapNodeToKey));
    this.selectedKeys.set(new Set<string>(keys));
  }

  // Set/Unset clicked file/folder as seleted item
  toggleSelectSingleNode(node: FileSystemTreeNode) {
    switch (this.operationMode()) {
      case OperationMode.Import: {
        const selectedNodesUpdate = this.selectedKeys().has(node.key) ? [] : [node];
        this.updateSelectedNodes(selectedNodesUpdate);
      }
      break;

      case OperationMode.Export: {
        if (node.data.type === FileSystemObjectType.Volume || node.data.type === FileSystemObjectType.Folder) {
          // Toggle selected node and update file storage path
          this.folderPath.set(node.key);
        } else if (node.data.type === FileSystemObjectType.File) {
          const { fileName, folderPath } = getFilePathInfo(node.key);
          // Replace file name with clicked file name
          this.folderPath.set(folderPath);
          this.fileName.set(fileName);
        }

        this.updateSelectedNodes([node]);
      }
      break;
    }
  }

  toggleSelectMultipleNodes(node: FileSystemTreeNode) {
    const isValidSelection = this.validateSelection(node);

    if (!isValidSelection) {
      // Mark clicked node as unchecked
      node.checked = false;
      return;
    }

    let selected = structuredClone(this.selectedNodes());

    // Add/Remove current file/folder to/from selected items
    if (this.selectedKeys().has(node.key)) {
      selected = selected.filter(({ key }) => key !== node.key);
    } else {
      selected = selected.concat(node);
    }

    this.updateSelectedNodes(selected);
  }

  private toggleNodeSelect = (node: FileSystemTreeNode) => {
    const isValidSelection = this.validateSelection(node);

    if (!isValidSelection) {
      // Mark clicked node as unchecked
      node.checked = false;
      return;
    }

    const { isSingleSelectionMode } = this.currentSelectionMode;

    // Update file/folder selection
    isSingleSelectionMode
      ? this.toggleSelectSingleNode(node)
      : this.toggleSelectMultipleNodes(node);
  };

  private validateSelection(node: FileSystemTreeNode) {
    const {
      isFileSelectionMode,
      isFolderSelectionMode,
      isFileOrFolderSelectionMode
    } = this.currentSelectionMode;

    let isValidSelection = true;

    isValidSelection ||= node.data.type === FileSystemObjectType.File && (isFileSelectionMode || isFileOrFolderSelectionMode);

    isValidSelection ||= node.data.type === FileSystemObjectType.Folder && (isFolderSelectionMode || isFileOrFolderSelectionMode);

    isValidSelection ||= node.data.type === FileSystemObjectType.Volume && this.operationMode() === OperationMode.Export;

    return isValidSelection;
  }

}
